import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Radio, RadioGroup } from '@twilio-paste/radio-group';
import { Box } from '@twilio-paste/box';
import { Paragraph } from '@twilio-paste/paragraph';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { SiteLink } from '../../../components/SiteLink';
import { SidebarCategoryRoutes } from '../../../constants';
import Changelog from '@twilio-paste/radio-group/CHANGELOG.md';
import { verticalExample, defaultExample, requiredExample, horizontalExample, errorExample, disabledExample } from '../../../component-examples/RadioGroupExamples';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/radio-group"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/radio-group/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name="Radio Group" categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/radio-group" storybookUrl="/?path=/story/components-radio--radio" abstractUrl="https://share.goabstract.com/6cc5fc40-3408-4cfd-b1d0-103aea01601d" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Guidelines`}</h2>
        <Paragraph mdxType="Paragraph">{props.pageContext.frontmatter.description}</Paragraph>
        <h3>{`About Radio Group`}</h3>
        <p>{`Radio groups are used when a user has a single choice to make from two or more options. The user can only choose one radio selection at a time.`}</p>
        <p>{`Radio groups should:`}</p>
        <ul>
          <li parentName="ul">{`Include at least two or more options`}</li>
          <li parentName="ul">{`Allow users to select only one option`}</li>
          <li parentName="ul">{`Include at most 6 options (use your discretion here if you need more; if there's a chance in the future that it might be more than 6, use a Select component drop-down) If there is help text that the user needs to know before making selection, use checkbox/radio.`}</li>
          <li parentName="ul">{`List options in a rational order that makes logical sense`}</li>
        </ul>
        <h4>{`Best Practices`}</h4>
        <ul>
          <li parentName="ul">{`See `}<a parentName="li" {...{
              "href": "/content"
            }}>{`content guidelines`}</a>{` for copy requirements`}</li>
          <li parentName="ul">{`Avoid nesting radio buttons with other radio buttons`}</li>
          <li parentName="ul">{`If an option is strongly recommended, add "(Recommended)" to the label. Be sure to add to the label, not the help text`}</li>
          <li parentName="ul">{`See `}<a parentName="li" {...{
              "href": "#default-pre-selected-state"
            }}>{`Default Pre-Selected`}</a>{` State to determine if you should have a default selection`}</li>
          <li parentName="ul">{`Use the same name attribute on all radio buttons in the group. This ensures that if there is more than one group in the form, each one stays associated with its own group`}</li>
          <li parentName="ul">{`If a radio group is “Optional”, users won't be able to unselect a selected radio. If you need to give users a way to unselect, consider adding a clear button or using another type of form field.`}</li>
          <li parentName="ul">{`Use a Select Component if there is a chance additional options over 6 will be added in the future.`}</li>
        </ul>
        <h3>{`Accessibility Information`}</h3>
        <ul>
          <li parentName="ul">{`A radio group must have a visible label that is in close proximity to the control`}</li>
          <li parentName="ul">{`HTML radio groups don't natively support the `}<inlineCode parentName="li">{`required`}</inlineCode>{` attribute. In cases where a radio is required to be checked:`}<ul parentName="li">
              <li parentName="ul">{`Display a required indicator`}</li>
              <li parentName="ul">{`Ensure the radio label text includes wording that successfully describes the requirement to the user that they should select the radio`}</li>
            </ul></li>
          <li parentName="ul">{`When in an error state, display an inline error message below the offending radio group that clearly describes the error.`}</li>
        </ul>
        <h2>{`Layout Examples`}</h2>
        <h3>{`Vertically Stacked Radio Group`}</h3>
        <p>{`Use this option if labels are too long (see horizontal alignment for limits) or the list needs to wrap to two lines.`}</p>
        <p>{`An optional default choice is pre-selected (see Default Pre-Selected State below).`}</p>
        <LivePreview scope={{
          Radio,
          RadioGroup
        }} noInline mdxType="LivePreview">
  {verticalExample}
        </LivePreview>
        <h3>{`Default Pre-Selected State`}</h3>
        <p>{`By default, we don't have a pre-selected state. It's at the discretion of Paste consumers to set a default selected option. If there is a pre-selected state, be sure to make the first option the default option. Please review this article to determine if you should default a selection: `}<a parentName="p" {...{
            "href": "https://www.nngroup.com/articles/radio-buttons-default-selection/"
          }}>{`https://www.nngroup.com/articles/radio-buttons-default-selection/`}</a>{`.`}</p>
        <p>{`Don't have a default selection if:`}</p>
        <ul>
          <li parentName="ul">{`A default value would incorrectly imply that the user doesn't need to make a selection when they actually need to make a choice.`}</li>
          <li parentName="ul">{`You are collecting unbiased data. Defaulting to an option biases or alters the experience in an undesired way and your data will be skewed`}</li>
          <li parentName="ul">{`There is no acceptable default option for safety, security, or legal reasons and therefore the user must make an explicit choice. (show display error)`}</li>
        </ul>
        <p>{`Component Example`}</p>
        <h3>{`With Help Text`}</h3>
        <p>{`You can provide additional information about the group with the use of help text. Help text can appear after the group label but before the first option. Use the group label to explain the purpose of the group, not how to make the selection. Assume that users know how to use radio buttons. For example, don't say "Select one of the following choices".`}</p>
        <LivePreview scope={{
          Radio,
          RadioGroup
        }} noInline mdxType="LivePreview">
  {defaultExample}
        </LivePreview>
        <h3>{`Required Usage`}</h3>
        <p>{`Use the required dot only when necessary to denote it is required. This goes before the fieldset legend.`}</p>
        <LivePreview scope={{
          Radio,
          RadioGroup
        }} noInline mdxType="LivePreview">
  {requiredExample}
        </LivePreview>
        <h3>{`Horizontal Alignment`}</h3>
        <ul>
          <li parentName="ul">{`Options are placed next to each other, in logical order, with appropriate spacing. If people can easily compare shorter options from left to right, use this alignment`}</li>
          <li parentName="ul">{`Do not run onto two lines`}</li>
          <li parentName="ul">{`If the label is longer than 3 words/20 characters, consider using a vertically stacked version. If the content is too long, please use vertical stacking`}</li>
        </ul>
        <LivePreview scope={{
          Radio,
          RadioGroup
        }} noInline mdxType="LivePreview">
  {horizontalExample}
        </LivePreview>
        <h2>{`States`}</h2>
        <h3>{`Disabled`}</h3>
        <LivePreview scope={{
          Radio
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Radio id="option" value="option" name="option" disabled>
    Option 1
  </Radio>
</>`}
        </LivePreview>
        <h3>{`Checked and disabled`}</h3>
        <LivePreview scope={{
          Radio
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Radio id="option" value="option" name="option" checked disabled>
    Option 2
  </Radio>
</>`}
        </LivePreview>
        <h3>{`Disabled group`}</h3>
        <LivePreview scope={{
          Radio,
          RadioGroup
        }} noInline mdxType="LivePreview">
  {disabledExample}
        </LivePreview>
        <h3>{`Group Error`}</h3>
        <p>{`If the selected items don't pass the group validation requirements an inline error message should be displayed.`}</p>
        <p>{`An inline error is placed at the bottom of the group to inform a user of any errors in their choices.`}</p>
        <LivePreview scope={{
          Radio,
          RadioGroup
        }} noInline mdxType="LivePreview">
  {errorExample}
        </LivePreview>
        <h3>{`Individual error`}</h3>
        <LivePreview scope={{
          Radio
        }} language="jsx" mdxType="LivePreview">
  {`<>
  <Radio id="criteria" value="criteria" name="criteria" checked hasError>
    Contacts no longer meet entry criteria
  </Radio>
</>`}
        </LivePreview>
        <h2>{`Composition notes`}</h2>
        <h2>{`When to use an Radio Group`}</h2>
        <p>{`Use a Radio Group to select a single option from a list of two, but no more than six (use your discretion) options.`}</p>
        <h2>{`Content Guidelines`}</h2>
        <Callout variant="warning" mdxType="Callout">
  🚨<strong>STOP!</strong>🚨 Before continuing, make sure to follow these{' '}
  <SiteLink to="/content" mdxType="SiteLink">content guidelines</SiteLink>.
        </Callout>
        <h3>{`Validation`}</h3>
        <p>{`Validate the group on form submission. Don't validate each item in the group, treat validation on the group as a whole.`}</p>
        <h3>{`Errors`}</h3>
        <p>{`Error text should go below groups.`}</p>
        <h3>{`Labels and Help Text`}</h3>
        <ul>
          <li parentName="ul">{`Radio text should not end in punctuation even if it's a sentence, but especially it it's a word or a fragment`}</li>
          <li parentName="ul">{`Use with an associated label component`}</li>
          <li parentName="ul">{`All labels begin with a capital letter`}</li>
        </ul>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/radio-group - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Radio, RadioGroup} from '@twilio-paste/radio-group';

const Component = () => (
  <RadioGroup name="foo" value="foo" legend="foo" onChange={NOOP}>
    <Radio id="foo" value="foo" name="foo">
      Foo
    </Radio>
  </RadioGroup>
);
`}</code></pre>
        <h4>{`Radio Props`}</h4>
        <p>{`All the valid HTML attributes for `}<inlineCode parentName="p">{`input[type=radio]`}</inlineCode>{` are supported including the following props:`}</p>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`id?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`value?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`hasError?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`helpText?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode>{` or `}<inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onChange?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onFocus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onBlur?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h4>{`RadioGroup Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`legend`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode>{` or `}<inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`name`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`value`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`orientation`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`oneOf(['vertical', 'horizontal'])`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`vertical`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`errorText?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode>{` or `}<inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`helpText?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`string`}</inlineCode>{` or `}<inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`disabled?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`required?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`false`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onChange`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onFocus?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`onBlur?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`(event: React.MouseEvent<HTMLElement>)`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      